import React, { useState, useContext, useRef, useEffect } from "react";
import clsx from "clsx";
import { createStyles, Theme, makeStyles, useTheme } from "@material-ui/core/styles";

import { Drawer, AppBar, CssBaseline, Toolbar, Typography, Divider, Hidden, IconButton, Grid, Avatar } from "@material-ui/core";
import MenuIcon from "@material-ui/icons/Menu";
import Pusher from "pusher-js";
import Gravatar from "react-gravatar";

import AppRouter, { AppMenu } from "../router/AppRouter";
import { AppContext } from "./AppContext";
import translate from "../i18n/Translator";
import TenantSwitch from "./TenantSwitch";
import DialogPopup from "../components/DialogPopup";
import { ErrorSnackbar, InfoSnackbar } from "../components/Snackbars";
import { WebSocketPayload } from "../model/WebSocket";
import CustomBackdrop from "../components/CustomBackdrop";
import AccountMenu from "./AccountMenu";

const drawerWidth = 260;
const avatarSize = 24;

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            display: "flex",
        },
        appBar: {
            zIndex: theme.zIndex.drawer + 1,
            borderWidth: 0,
            borderBottomWidth: "1px",
            [theme.breakpoints.up("md")]: {
                // width: `calc(100% - ${drawerWidth}px)`,
                // marginLeft: drawerWidth,
            },
        },
        drawer: {
            [theme.breakpoints.up("md")]: {
                width: drawerWidth,
                flexShrink: 0,
            },
        },
        drawerPaper: {
            width: drawerWidth,
        },
        content: {
            flexGrow: 1,
            paddingTop: theme.spacing(3),
            transition: theme.transitions.create("margin", {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.leavingScreen,
            }),
            marginLeft: -drawerWidth,
        },
        contentShift: {
            transition: theme.transitions.create("margin", {
                easing: theme.transitions.easing.easeOut,
                duration: theme.transitions.duration.enteringScreen,
            }),
            marginLeft: 0,
        },
        menuButton: {
            marginRight: theme.spacing(1),
            [theme.breakpoints.up("md")]: {
                display: "none",
            },
        },
        hiddenButton: {
            marginRight: theme.spacing(1),
            [theme.breakpoints.down("sm")]: {
                display: "none",
            },
        },
        title: {
            flexGrow: 1,
        },
        avatar: {
            width: avatarSize,
            height: avatarSize
        },
        toolbar: theme.mixins.toolbar
    }),
);

export default function InternApp(props: InternAppProps) {
    const context = useContext(AppContext);
    const classes = useStyles();
    const theme = useTheme();
    const [mobileOpen, setMobileOpen] = useState(false);
    const [desktopOpen, setDesktopOpen] = useState<boolean>(() => {
        let item = localStorage.getItem("ShowDrawer");
        let value = item !== "false";
        localStorage.setItem("ShowDrawer", `${value}`);
        return value;
    });
    const [blocked, setBlocked] = useState<boolean>(false);
    const [notification, setNotification] = useState<string>();

    const [accountOpen, setAccountOpen] = useState(false);
    const accountAnchorRef = useRef<HTMLButtonElement>(null);
    const prevAccountOpen = useRef(accountOpen);

    const pusher = (() => {
        if (!context.session?.websocket) return undefined;

        let websocket = context.session.websocket;
        if (websocket.name !== "pusher") return undefined;

        Pusher.logToConsole = websocket.verbose;

        let instance = new Pusher(websocket.key, {
            cluster: websocket.cluster,
            forceTLS: websocket.encrypted,
        });

        let channel = instance.subscribe(context.session!.secret_id);
        channel.bind(websocket.event_name, (payload: WebSocketPayload) => {
            if (payload.message.type === "BLOCKED_USER") {
                setBlocked(true);
            } else {
                setNotification(payload.message.message);
            }
        });

        return instance;
    })();

    const onSignOut = () => {
        if (pusher) pusher.disconnect();

        context.removeSession();
    };

    const handleToggleAccount = () => {
        setAccountOpen(prevOpen => !prevOpen);
    };

    const onCloseAccount = () => {
        setAccountOpen(false);
    };

    // return focus to the button when we transitioned from !open -> open
    useEffect(() => {
        if (prevAccountOpen.current === true && accountOpen === false) {
            accountAnchorRef.current!.focus();
        }
        prevAccountOpen.current = accountOpen;
    }, [accountOpen, props.error]);

    const handleMobileToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    const handleDesktopToggle = () => {
        let value = !desktopOpen;
        setDesktopOpen(!desktopOpen);
        localStorage.setItem("ShowDrawer", `${value}`);
    };

    const drawer = (
        <div>
            <Toolbar className={classes.toolbar}>
                <Grid container justify="center">
                    <Grid item xs="auto">
                        <img src="/logo_white.png" alt="logo" className="InternLogo" />
                    </Grid>
                </Grid>
            </Toolbar>
            <Divider />
            <TenantSwitch />
            {context.session && context.session.customer && (
                <Grid container justify="center">
                    <Grid item xs={10}>
                        <Typography variant="caption" component="h6" color="textSecondary">
                            {translate("app.customer")}
                        </Typography>
                    </Grid>
                    <Grid item xs={10}>
                        <Typography variant="body1" component="h6" color="textPrimary">
                            {context.session.customer.name}
                        </Typography>
                    </Grid>
                </Grid>
            )}
            <AppMenu />
        </div>
    );

    const router = (<AppRouter />);

    return (
        <div className={classes.root}>
            <CssBaseline />
            <AppBar position="fixed" className={classes.appBar} color="primary" variant="outlined">
                <Toolbar className={classes.toolbar}>
                    <IconButton aria-label="open drawer" edge="start" color="inherit" onClick={handleMobileToggle} className={classes.menuButton} >
                        <MenuIcon />
                    </IconButton>
                    <IconButton aria-label="open drawer" edge="start" color="inherit" onClick={handleDesktopToggle} className={classes.hiddenButton} >
                        <MenuIcon />
                    </IconButton>
                    <div className={classes.title}>
                        <img src="/logo_white.png" alt="logo" className="InternLogo" />
                    </div>
                    <IconButton ref={accountAnchorRef} aria-controls={accountOpen ? "account-menu" : undefined} edge="end" aria-haspopup="true" color="inherit" size="small" onClick={handleToggleAccount}>
                        <Avatar className={classes.avatar}>
                            <Gravatar email={context.session!.user.email} size={avatarSize} default="identicon" />
                        </Avatar>
                    </IconButton>
                    {accountOpen && (
                        <AccountMenu anchorRef={accountAnchorRef} onClose={onCloseAccount} onSignOut={onSignOut} />
                    )}
                </Toolbar>
            </AppBar>
            <nav className={classes.drawer} aria-label="mailbox folders">
                <Hidden mdUp implementation="css">
                    <Drawer variant="temporary" anchor={theme.direction === "rtl" ? "right" : "left"} open={mobileOpen} onClose={handleMobileToggle}
                        classes={{ paper: classes.drawerPaper }} ModalProps={{ keepMounted: true }}>
                        {drawer}
                    </Drawer>
                </Hidden>
                <Hidden smDown implementation="css">
                    <Drawer variant="persistent" open={desktopOpen} classes={{ paper: classes.drawerPaper, }}>
                        {drawer}
                    </Drawer>
                </Hidden>
            </nav>
            <main className={clsx(classes.content, {
                [classes.contentShift]: desktopOpen,
            })}>
                <div className={classes.toolbar} />
                <Grid container className={desktopOpen ? "WithDrawer" : undefined}>
                    <Grid item xs={12}>
                        {router}
                    </Grid>
                </Grid>
            </main>
            <ErrorSnackbar message={props.error} onClose={() => props.onCleanError()} />
            <InfoSnackbar message={notification} onClose={() => setNotification(undefined)} />
            <CustomBackdrop open={props.backdrop} />
            <DialogPopup open={blocked} title={translate("users.blocked.title")} onClose={onSignOut} closeText={translate("buttons.signout") as string}>
                {translate("users.blocked.description")}
            </DialogPopup>
        </div>
    );
}

export interface InternAppProps {
    backdrop: boolean;
    error?: string;
    onCleanError(): any;
}